import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Truck, Lock, ArrowLeft, Plus, Route as RouteIcon } from 'lucide-react';
import nurseryService from '../../services/nursery';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useOrgTimezone, getTodayInTz, formatLocalDateInTz } from '../../utils/timezone';
import { errorMessage } from '../../utils/apiResponse';
import { cn } from '../../utils/cn';
import { LoadStatusBadge } from '../../components/nursery/despatch/LoadStatusBadge';
import { NewLoadDialog } from '../../components/nursery/despatch/NewLoadDialog';
import type { LoadFieldsValue } from '../../components/nursery/despatch/LoadFields';
import { type NurseryDeliveryZone, type NurseryLoad } from '../../types/nursery';

/** /nursery/loads — every load, open ones first by date. */

type Filter = 'open' | 'all';

function LoadList() {
  const { t } = useTranslation('nursery', { keyPrefix: 'LoadList' });
  const navigate = useNavigate();
  const tz = useOrgTimezone();
  const today = getTodayInTz(tz);
  const { hasAccess: canManage, loading: licenseLoading } = useModuleLicense('nursery', ['manager', 'admin']);

  const [filter, setFilter] = useState<Filter>('open');
  const [loads, setLoads] = useState<NurseryLoad[]>([]);
  const [farms, setFarms] = useState<{ id: number; name: string; picker_count: number }[]>([]);
  const [zones, setZones] = useState<NurseryDeliveryZone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newLoad, setNewLoad] = useState<LoadFieldsValue | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await nurseryService.getLoads(filter === 'open' ? { open: true } : undefined);
      if (res.success) setLoads(res.data || []);
      else setError(errorMessage(res, t('loadFailed')));
    } catch (e) {
      setError(errorMessage(e, t('loadFailed')));
    } finally {
      setLoading(false);
    }
  }, [filter, t]);

  useEffect(() => {
    if (canManage) load();
  }, [canManage, load]);

  const openNew = async () => {
    try {
      // The pickers payload carries the farms, and who picks at each.
      const [pick, zoneRes] = await Promise.all([
        nurseryService.getPickers(),
        nurseryService.getDeliveryZones(),
      ]);
      const f = pick.success
        ? pick.data.farms.map((farm) => ({
          ...farm,
          picker_count: pick.data.pickers.filter((p) => p.farm_ids.includes(farm.id)).length,
        }))
        : [];
      setFarms(f);
      if (zoneRes.success) setZones(zoneRes.data || []);
      setNewLoad({ load_type: 'run', despatch_farm_id: f.length === 1 ? f[0].id : 0, despatch_date: today, zone_ids: [] });
    } catch (e) {
      setError(errorMessage(e, t('openNewFailed')));
    }
  };

  if (licenseLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="h-8 bg-neutral-100 rounded animate-pulse w-64 mb-4" />
        <div className="h-40 bg-neutral-100 rounded animate-pulse" />
      </div>
    );
  }

  if (!canManage) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
          <Lock className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-neutral-800 mb-1">{t('licenseTitle')}</h2>
          <p className="text-sm text-neutral-500 mb-4">
            <Trans t={t} i18nKey="licenseBody" components={{ b: <span className="font-medium" /> }} />
          </p>
          <Button variant="outline" size="sm" onClick={() => navigate('/nursery')}>
            <ArrowLeft className="h-4 w-4 mr-1" /> {t('backToNursery')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        icon={<Truck className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/nursery/despatch')}>
              <RouteIcon className="h-4 w-4" /> {t('planner')}
            </Button>
            <Button size="sm" onClick={openNew}>
              <Plus className="h-4 w-4" /> {t('newLoad')}
            </Button>
          </div>
        }
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        <div className="flex gap-2" role="group" aria-label={t('whichLoads')}>
          {(['open', 'all'] as Filter[]).map((f) => (
            <button key={f} type="button" aria-pressed={filter === f} onClick={() => setFilter(f)}
              className={cn('rounded-lg border px-3 py-1.5 text-sm',
                filter === f ? 'bg-primary-50 border-primary-400 text-primary-800 font-medium' : 'bg-white border-neutral-300 text-neutral-600')}>
              {f === 'open' ? t('filterOpen') : t('filterAll')}
            </button>
          ))}
        </div>

        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{error}</div>}

        {loading ? (
          <div className="h-40 bg-neutral-100 rounded-xl animate-pulse" />
        ) : loads.length === 0 ? (
          <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center text-sm text-neutral-500">
            <Trans t={t} i18nKey="empty" components={{ link: <Link to="/nursery/despatch" className="text-primary-600 hover:underline" /> }} />
          </div>
        ) : (
          <ul className="space-y-3">
            {loads.map((l) => {
              const over = l.capacity_trees != null && l.total_trees > l.capacity_trees;
              return (
                <li key={l.id}>
                  <Link to={`/nursery/loads/${l.id}`}
                    className="block bg-white border border-neutral-200 rounded-xl p-4 hover:border-primary-300">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="font-semibold text-neutral-900">{l.load_number}</span>
                      <span className="text-sm text-neutral-600">
                        {l.is_daily ? t('typeDaily', { type: t(`loadType.${l.load_type}`) }) : t(`loadType.${l.load_type}`)}
                      </span>
                      <LoadStatusBadge status={l.status} />
                      <span className="ml-auto text-sm font-medium text-neutral-800">{formatLocalDateInTz(l.despatch_date, tz)}</span>
                    </div>
                    <div className="mt-1 text-xs text-neutral-500 flex flex-wrap gap-x-3 gap-y-0.5">
                      <span className="break-words min-w-0">{t('from', { farm: l.despatch_farm_name })}</span>
                      <span>{t('orders', { count: l.order_count ?? 0 })}</span>
                      <span className={cn(over && 'text-amber-700 font-medium')}>
                        {l.capacity_trees
                          ? t('treesOfCapacity', { trees: l.total_trees.toLocaleString(), capacity: l.capacity_trees.toLocaleString() })
                          : t('trees', { trees: l.total_trees.toLocaleString() })}
                      </span>
                      {l.zone_names.length > 0 && <span className="break-words min-w-0">{l.zone_names.join(', ')}</span>}
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {newLoad && (
        <NewLoadDialog
          initial={newLoad}
          orderIds={[]}
          trees={0}
          farms={farms}
          zones={zones}
          onClose={() => setNewLoad(null)}
          onCreated={(l) => navigate(`/nursery/loads/${l.id}`)}
        />
      )}
    </>
  );
}

export default LoadList;
